import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../../../components/Layout';
import { restaurantTableService } from '../../../../services/restaurantTableService';
import { ArrowLeft, LayoutGrid, AlertCircle, Zap } from 'lucide-react';
import { getServerErrorMessage } from '../../../../utils/errorUtils';
import '../RestaurantPage.css';

interface FormData {
  tableNumber: string;
  name:        string;
  capacity:    number;
  section:     string;
  shape:       string;
  positionX:   number;
  positionY:   number;
  width:       number;
  height:      number;
  isActive:    boolean;
}

const SHAPES = ['Rectangle', 'Round', 'Square'];

const TableCreatePage: React.FC = () => {
  const navigate   = useNavigate();
  const { id }     = useParams<{ id: string }>();
  const { t }      = useTranslation();
  const isEdit     = Boolean(id);

  const [saving,      setSaving]      = useState(false);
  const [loadingData, setLoadingData] = useState(isEdit);
  const [error,       setError]       = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [formData,    setFormData]    = useState<FormData>({
    tableNumber: '', name: '', capacity: 2, section: '', shape: 'Rectangle',
    positionX: 0, positionY: 0, width: 80, height: 60, isActive: true,
  });

  useEffect(() => {
    if (!isEdit || !id) return;
    setLoadingData(true);
    restaurantTableService.getById(parseInt(id))
      .then(res => {
        const d = (res.data as any)?.data ?? res.data;
        setFormData({
          tableNumber: d.tableNumber ?? '',
          name:        d.name        ?? '',
          capacity:    d.capacity    ?? 2,
          section:     d.section     ?? '',
          shape:       d.shape       ?? 'Rectangle',
          positionX:   d.positionX   ?? 0,
          positionY:   d.positionY   ?? 0,
          width:       d.width       ?? 80,
          height:      d.height      ?? 60,
          isActive:    d.isActive    ?? true,
        });
      })
      .catch(() => setError(t('table.failedToLoad', 'Failed to load table.')))
      .finally(() => setLoadingData(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const set = (field: keyof FormData) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      const { value, type } = e.target;
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(p => ({ ...p, [field]: type === 'checkbox' ? checked : type === 'number' ? Number(value) : value }));
      setFieldErrors(p => ({ ...p, [field]: undefined }));
    };

  const validate = () => {
    const errs: Partial<Record<keyof FormData, string>> = {};
    if (!formData.tableNumber.trim()) errs.tableNumber = t('common.required');
    if (!formData.name.trim())        errs.name        = t('common.required');
    if (formData.capacity < 1)        errs.capacity    = t('table.capacityMin', 'Minimum 1');
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true); setError(null);
    const payload = {
      tableNumber: formData.tableNumber.trim(),
      name:        formData.name.trim(),
      capacity:    formData.capacity,
      section:     formData.section.trim() || undefined,
      shape:       formData.shape,
      positionX:   formData.positionX,
      positionY:   formData.positionY,
      width:       formData.width,
      height:      formData.height,
      isActive:    formData.isActive,
    };
    try {
      if (isEdit && id) await restaurantTableService.update(parseInt(id), payload);
      else              await restaurantTableService.create(payload);
      navigate('/restaurant/tables');
    } catch (err: any) {
      setError(getServerErrorMessage(err) ?? t('table.failedToSave', 'Failed to save table.'));
      setSaving(false);
    }
  };

  if (loadingData) {
    return (
      <Layout>
        <div className="ims-create-body-page rest-create-page" style={{ alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
          <div className="spinner-border" role="status"><span className="visually-hidden">{t('common.loading')}</span></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <form onSubmit={handleSubmit} className="rest-create-page">
        <div className="ims-create-body-page">

          <div className="ims-top-bar">
            <div className="ims-top-bar-left">
              <button type="button" className="ims-back-btn" onClick={() => navigate('/restaurant/tables')}>
                <ArrowLeft size={13} /> {t('table.title')}
              </button>
              <div>
                <h1 className="ims-page-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <LayoutGrid size={18} style={{ color: 'var(--ims-accent)' }} />
                  {isEdit ? t('table.editTitle', 'Edit Table') : t('table.createTitle', 'New Table')}
                </h1>
                <p className="ims-page-subtitle">
                  {isEdit ? t('table.editSubtitle', 'Update the table details below') : t('table.createSubtitle', 'Define a new table for your restaurant floor')}
                </p>
              </div>
            </div>
            <div className="ims-top-bar-right">
              <button type="button" className="ims-btn ims-btn-secondary" onClick={() => navigate('/restaurant/tables')} disabled={saving}>
                {t('common.cancel')}
              </button>
              <button type="submit" className="ims-btn ims-btn-primary" disabled={saving}>
                {saving
                  ? <><span className="spinner-border spinner-border-sm me-1" role="status" />{t('common.saving')}</>
                  : (isEdit ? t('common.update') : t('common.save'))}
              </button>
            </div>
          </div>

          {error && (
            <div className="ims-error-banner">
              <div className="ims-alert ims-alert-error" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <AlertCircle size={15} />
                <span style={{ flex: 1 }}>{error}</span>
                <button type="button" className="btn-close" onClick={() => setError(null)} />
              </div>
            </div>
          )}

          <div className="ims-create-body ims-create-body-single">
            <div className="ims-panel-center" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

              {/* Identity */}
              <div className="ims-card mi-card">
                <div className="ims-card-header mi-card-header">
                  <LayoutGrid size={14} className="mi-header-icon" />
                  <span className="ims-card-title">{t('table.details', 'Table Details')}</span>
                </div>
                <div className="ims-card-body mi-card-body">

                  <div className="mi-row-2">
                    <div className={`mi-field${fieldErrors.tableNumber ? ' mi-field-error' : ''}`}>
                      <label className="mi-label">{t('table.tableNumber')} <span className="mi-required">*</span></label>
                      <input type="text" className="mi-input" value={formData.tableNumber} onChange={set('tableNumber')}
                        placeholder="e.g. T01, A1" maxLength={20} autoFocus />
                      {fieldErrors.tableNumber && <span className="mi-err">{fieldErrors.tableNumber}</span>}
                    </div>
                    <div className={`mi-field${fieldErrors.name ? ' mi-field-error' : ''}`}>
                      <label className="mi-label">{t('table.tableName')} <span className="mi-required">*</span></label>
                      <input type="text" className="mi-input" value={formData.name} onChange={set('name')}
                        placeholder="e.g. Window Table" maxLength={100} />
                      {fieldErrors.name && <span className="mi-err">{fieldErrors.name}</span>}
                    </div>
                  </div>

                  <div className="mi-row-2">
                    <div className={`mi-field${fieldErrors.capacity ? ' mi-field-error' : ''}`}>
                      <label className="mi-label">{t('table.capacity')}</label>
                      <input type="number" className="mi-input" value={formData.capacity} onChange={set('capacity')} min={1} max={50} />
                      {fieldErrors.capacity && <span className="mi-err">{fieldErrors.capacity}</span>}
                    </div>
                    <div className="mi-field">
                      <label className="mi-label">{t('table.section')}</label>
                      <input type="text" className="mi-input" value={formData.section} onChange={set('section')}
                        placeholder="e.g. Indoor, Outdoor, Terrace" maxLength={100} />
                    </div>
                  </div>

                  <div className="mi-field">
                    <label className="mi-label">{t('table.shape')}</label>
                    <select className="mi-input" value={formData.shape} onChange={set('shape')}>
                      {SHAPES.map(s => (
                        <option key={s} value={s}>{t(`table.shapes.${s}`, s)}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Floor plan dimensions */}
              <div className="ims-card mi-card">
                <div className="ims-card-header mi-card-header">
                  <LayoutGrid size={14} className="mi-header-icon" />
                  <span className="ims-card-title">{t('table.floorPlan', 'Floor Plan')}</span>
                </div>
                <div className="ims-card-body mi-card-body">
                  <div className="mi-row-2">
                    <div className="mi-field">
                      <label className="mi-label">{t('table.positionX', 'Position X')}</label>
                      <input type="number" className="mi-input" value={formData.positionX} onChange={set('positionX')} min={0} />
                    </div>
                    <div className="mi-field">
                      <label className="mi-label">{t('table.positionY', 'Position Y')}</label>
                      <input type="number" className="mi-input" value={formData.positionY} onChange={set('positionY')} min={0} />
                    </div>
                  </div>
                  <div className="mi-row-2">
                    <div className="mi-field">
                      <label className="mi-label">{t('table.width', 'Width (px)')}</label>
                      <input type="number" className="mi-input" value={formData.width} onChange={set('width')} min={40} max={300} />
                    </div>
                    <div className="mi-field">
                      <label className="mi-label">{t('table.height', 'Height (px)')}</label>
                      <input type="number" className="mi-input" value={formData.height} onChange={set('height')} min={40} max={300} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Status */}
              <div className="ims-card mi-card">
                <div className="ims-card-header mi-card-header">
                  <Zap size={14} className="mi-header-icon" />
                  <span className="ims-card-title">{t('common.status')}</span>
                </div>
                <div className="ims-card-body mi-card-body">
                  <label className={`mi-toggle-row${formData.isActive ? ' mi-toggle-on' : ''}`}>
                    <div className="mi-toggle-text">
                      <span className="mi-toggle-label">{t('common.active')}</span>
                      <span className="mi-toggle-sub">{t('table.activeDesc', 'Make this table available for orders and reservations')}</span>
                    </div>
                    <div className={`mi-switch${formData.isActive ? ' mi-switch-on' : ''}`}
                      onClick={() => setFormData(p => ({ ...p, isActive: !p.isActive }))}>
                      <div className="mi-switch-thumb" />
                    </div>
                  </label>
                </div>
              </div>

            </div>
          </div>
        </div>
      </form>
    </Layout>
  );
};

export default TableCreatePage;
